import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import Divider from '@material-ui/core/Divider';

import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';

import {
  getDNIField,
  getCedulaSerialNumber,
  getRegionField,
  getCommuneField,
  getAddressNumberField,
  getAddressField,
  getEmailField,
  getPhoneField,
  getAttachedField,
  getAllowEndorsementField
} from './fields';

import CommuneService from '../../../services/commune-service';
import RegionService from '../../../services/region-service';
import AddressService from '../../../services/address-service';
import { useLoadingBar } from '../../../libraries/contexts';
import { SUBSCRIBER_FORM_TITLE, ADDRESS_NO_NORMALIZED } from '../../../libraries/texts';
import { isValidMinimumDNI } from '../../../libraries/validators';
import { getFingerPrintField } from '../../../components/fields/finger';

import { subscriber as styles } from '../styles';

const SubscriberForm = props => {
  const { values, classes, handleBlur, errors, touched, setFieldValue, setFieldTouched } = props;
  const [showCedula, setShowCedula] = useState(false);
  const [loading, setLoading] = useLoadingBar();
  const [regions, setRegions] = useState([]);
  const [communes, setCommunes] = useState([]);
  const prefix = 'suscriptor';

  const getRequests = async () => {
    setLoading({ principalLoading: true });
    const regions = await RegionService.getAll();
    setRegions(regions);
    setLoading({ principalLoading: false });
  };

  const changeCommunes = async () => {
    setLoading({ principalLoading: true });
    const regionVal = values[`${prefix}Region`];
    const communes = await CommuneService.perRegion(regionVal._id);
    setCommunes(communes);
    setLoading({ principalLoading: false });
  };

  const normalizeAddress = async () => {
    if (
      !values[`${prefix}Commune`] ||
      !values[`${prefix}Address`] ||
      !values[`${prefix}AddressNumber`]
    ) {
      return;
    }
    const data = AddressService.makeNormalizationData(
      values[`${prefix}Commune`] && values[`${prefix}Commune`].code
        ? values[`${prefix}Commune`].name
        : '',
      values[`${prefix}Address`],
      values[`${prefix}AddressNumber`],
      values[`${prefix}Anexo`]
    );
    let res = {};
    try {
      res = await AddressService.normalization(data);
    } catch (e) {
      setFieldValue(`is${prefix}Normalized`, false);
      return;
    }

    const { extra, number, commune, address, isNormalized = true } = res;
    setFieldValue(`is${prefix}Normalized`, isNormalized);

    if (!!address && values[`${prefix}Address`] !== address) {
      setFieldValue(`${prefix}Address`, address);
    }
    if (
      commune &&
      commune.code &&
      values[`${prefix}Commune`] &&
      values[`${prefix}Commune`].code &&
      commune.code !== values[`${prefix}Commune`].code
    ) {
      setFieldValue(`${prefix}Commune`, commune);
    }
    if (values[`${prefix}Anexo`] !== extra) {
      if (extra !== 'UNDEFINED') {
        setFieldValue(`${prefix}Anexo`, extra);
      } else {
        setFieldValue(`${prefix}Anexo`, '');
      }
    }
    if (!!number && values[`${prefix}AddressNumber`] !== number) {
      setFieldValue(`${prefix}AddressNumber`, number);
    }
  };

  useEffect(() => {
    [...document.getElementsByTagName('circle')].map(elem => {
      if (!elem.parentElement.parentElement.parentElement.title) {
        return elem.setAttribute('r', 35);
      }
      return elem;
    });
    getRequests();
  }, []);

  useEffect(() => {
    const regionVal = values[`${prefix}Region`];
    setCommunes([]);
    if (!regionVal || !regionVal.code) {
      setFieldValue(`${prefix}Commune`, '');
      return;
    }
    setFieldValue(`${prefix}Commune`, '');
    changeCommunes();
  }, [values[`${prefix}Region`]]);

  useEffect(() => {
    normalizeAddress();
  }, [values[`${prefix}normalizeAddress`]]);

  useEffect(() => {
    if (!values['allowEndorsement']) {
      const value = 'aval';
      const fields = [
        'AddressNumber',
        'Cedula',
        'DniNumber',
        'Address',
        'Region',
        'Commune',
        'Dni',
        'Anexo',
        'DniDisabled',
        'capturedFingerprint',
        'Email',
        'HasSerialNumber',
        'Tel1',
        'Tel2'
      ];

      setFieldValue('avalStatus', 5);
      setFieldValue('avalMessage', '');
      setFieldValue(`avalBeforeDni`, '');
      for (let name of fields) {
        setFieldValue(`${value}${name}`, '');
      }

      setFieldValue('isavalNormalized', true);
    }
  }, [values['allowEndorsement']]);

  const fieldProps = {
    handleBlur,
    classes,
    touched,
    errors,
    setFieldTouched,
    values,
    setFieldValue,
    prefix
  };

  return (
    <Card className={classes.card}>
      <CardHeader title={SUBSCRIBER_FORM_TITLE} className={classes.cardTitle} />
      <CardContent className={classes.cardContent}>
        {values ? (
          <React.Fragment>
            <div className={classes.middleWidthContainer}>
              {getDNIField({
                ...fieldProps,
                name: 'suscriptorDni',
                label: 'Rut suscriptor',
                disabled: values[`${prefix}DniDisabled`],
                formControlClass: classes.fullWidth,
                setShowCedula
              })}
              {getCedulaSerialNumber({
                ...fieldProps,
                name: 'suscriptorCedula',
                label: 'Número de Serie Cédula suscriptor',
                formControlClass: classes.fullWidth,
                showCedula,
                isMinDigitsDNI: !isValidMinimumDNI(values['suscriptorDni'])
              })}
            </div>
            {getFingerPrintField({ ...fieldProps, available: showCedula })}
            <Divider />
            {getRegionField({
              ...fieldProps,
              name: 'suscriptorRegion',
              label: 'Región *',
              options: regions,
              loading
            })}
            {getCommuneField({
              ...fieldProps,
              name: 'suscriptorCommune',
              label: 'Comuna *',
              options: communes,
              loading
            })}
            {getAddressField({ ...fieldProps, name: 'suscriptorAddress', label: 'Dirección' })}
            {getAddressNumberField({
              ...fieldProps,
              name: 'suscriptorAddressNumber',
              label: 'Número Dirección',
              required: true
            })}
            {getAttachedField({ ...fieldProps, name: 'suscriptorAnexo', label: 'Anexo' })}
            {!values.issuscriptorNormalized ? (
              <div className={classNames(classes.fullWidthError)}>{ADDRESS_NO_NORMALIZED}</div>
            ) : (
              ''
            )}
            <Divider />
            {getEmailField({
              ...fieldProps,
              name: 'suscriptorEmail',
              label: 'Email Suscriptor'
            })}
            {getPhoneField({
              ...fieldProps,
              name: 'suscriptorTel1',
              label: 'Teléfono contacto 1',
              required: true
            })}
            {getPhoneField({
              ...fieldProps,
              name: 'suscriptorTel2',
              label: 'Teléfono contacto 2',
              required: true
            })}
            <Divider />
            {getAllowEndorsementField({
              ...fieldProps,
              name: 'allowEndorsement',
              label: 'Agregar Aval',
              required: false
            })}
          </React.Fragment>
        ) : (
          ''
        )}
      </CardContent>
    </Card>
  );
};

SubscriberForm.propTypes = {
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(SubscriberForm);
